import React from 'react';
import {
  Inbox as InboxIcon, Tag, Wand2, Plug, BarChart2, ThumbsDown,
  Database, Globe, Bot, Send, GitMerge, History, Archive,
  ArrowRight, ArrowDown, Layers, Cog, Sparkles, ShieldAlert, Activity,
} from 'lucide-react';
import AppShell from '../components/AppShell';

const Box = ({ icon: Icon, title, subtitle, tone = 'neutral', wide = false }) => {
  const map = {
    neutral:  'bg-white border-border',
    source:   'bg-stone-50 border-stone-300',
    replai:   'bg-[hsl(var(--accent))] border-[hsl(var(--primary)/0.35)]',
    ai:       'bg-violet-50 border-violet-200',
    human:    'bg-emerald-50 border-emerald-200',
    external: 'bg-amber-50 border-amber-200',
  };
  return (
    <div
      className={`rounded-2xl border ${map[tone]} px-4 py-3 shadow-sm ${wide ? 'min-w-[16rem]' : 'min-w-[11rem]'}`}
    >
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className="h-4 w-4 shrink-0 text-[hsl(var(--primary))]" />}
        <p className="font-semibold text-sm text-foreground">{title}</p>
      </div>
      {subtitle && <p className="text-xs text-muted-foreground leading-snug">{subtitle}</p>}
    </div>
  );
};

const Arrow = ({ direction = 'right', label }) => {
  const Icon = direction === 'down' ? ArrowDown : ArrowRight;
  return (
    <div className={`flex ${direction === 'down' ? 'flex-col' : 'flex-row'} items-center justify-center text-muted-foreground`}>
      <Icon className="h-5 w-5" />
      {label && <span className="text-[0.65rem] mx-1">{label}</span>}
    </div>
  );
};

const Section = ({ title, children, subtitle }) => (
  <section className="bg-white rounded-2xl border border-border shadow-sm p-6 mb-6">
    <div className="mb-4">
      <h3 className="font-display text-xl font-semibold text-foreground">{title}</h3>
      {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
    </div>
    {children}
  </section>
);

const Architecture = () => {
  return (
    <AppShell
      title="Architecture"
      subtitle="How Replai is wired: data sources, ingestion, classification, drafting, review. Everything in one place."
    >
      <Section
        title="1 · End-to-end ticket flow"
        subtitle="A customer email enters Gorgias and ends up as a Replai draft - optionally auto-posted as an internal note when confidence is high enough."
      >
        <div className="flex items-start gap-3 overflow-x-auto pb-2">
          <Box icon={Globe} title="Gorgias view" subtitle="Open tickets in view 149731" tone="external" />
          <Arrow />
          <Box icon={InboxIcon} title="Ingestion" subtitle="60s poll · thread fetch · dedupe" tone="replai" />
          <Arrow />
          <Box icon={Tag} title="Classifier" subtitle="DB rules + subject fallback" tone="replai" />
          <Arrow />
          <Box icon={ShieldAlert} title="LLM Triage" subtitle="When classifier uncertain · escalates to human if needed" tone="ai" />
          <Arrow />
          <Box icon={Bot} title="Tool selection" subtitle="Claude tool_use picks endpoints + args" tone="ai" />
          <Arrow />
          <Box icon={Plug} title="Endpoints" subtitle="Orders · Invoices · PIM · Shipping · B2B" tone="replai" />
          <Arrow />
          <Box icon={Bot} title="Claude Haiku draft" subtitle="Customer reply OR agent action note" tone="ai" />
          <Arrow />
          <Box icon={Send} title="Auto-post or review" subtitle="High-confidence → auto note · else human reviews" tone="human" />
        </div>
      </Section>

      <Section
        title="1b · Agentic evolution (phases)"
        subtitle="Replai's decision layer has three planned levels of autonomy. Phase 3 is on hold; phases 1 & 2 ship."
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <ShieldAlert className="h-4 w-4 text-emerald-700" />
              <p className="font-semibold text-sm">Phase 1 · Triage</p>
              <span className="text-[0.6rem] text-emerald-700 uppercase">shipped</span>
            </div>
            <p className="text-xs text-muted-foreground leading-snug">
              When the rule-based classifier is uncertain, Claude Haiku gets a first pass: pick a category,
              decide if AI should even attempt a reply, or short-circuit to a human. Saves downstream endpoint
              calls on clearly-human tickets (legal, angry customers, no data).
            </p>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Bot className="h-4 w-4 text-emerald-700" />
              <p className="font-semibold text-sm">Phase 2 · Tool-use selection</p>
              <span className="text-[0.6rem] text-emerald-700 uppercase">shipped</span>
            </div>
            <p className="text-xs text-muted-foreground leading-snug">
              Each endpoint is a Claude tool with a typed schema. Claude picks which to call and extracts
              the arguments (order #, product name, invoice ref, country) from the raw message - richer than
              regex. One round of tool_use per ticket; no iteration yet.
            </p>
          </div>
          <div className="rounded-xl border border-stone-200 bg-stone-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-stone-500" />
              <p className="font-semibold text-sm">Phase 3 · Agent loop</p>
              <span className="text-[0.6rem] text-stone-500 uppercase">on hold</span>
            </div>
            <p className="text-xs text-muted-foreground leading-snug">
              Full tool_use feedback loop: Claude sees endpoint results and decides whether to call more
              tools (e.g. "order wasn't found, try by-email"). Intentionally deferred until phase 2 is
              proven safe.
            </p>
          </div>
        </div>
      </Section>

      <Section
        title="1c · Automations (feature-flagged)"
        subtitle="Each automation is OFF by default and writes only to LampTwist's own Gorgias workspace. Toggled via /api/replai/settings/."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 mb-2">
              <InboxIcon className="h-4 w-4 text-indigo-600" />
              <p className="font-semibold text-sm">auto_dispatch_enabled</p>
            </div>
            <p className="text-xs text-muted-foreground leading-snug">
              Every newly ingested ticket triggers <code className="text-[0.65rem]">run_and_save()</code> so
              no inbox item stays "untreated". Re-ingests are skipped.
            </p>
          </div>
          <div className="rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 mb-2">
              <Send className="h-4 w-4 text-indigo-600" />
              <p className="font-semibold text-sm">auto_note_enabled + auto_note_min_confidence</p>
            </div>
            <p className="text-xs text-muted-foreground leading-snug">
              After a successful <code className="text-[0.65rem]">run_and_save()</code>, post the draft as
              an internal Gorgias note when status=ready, audience=customer, and self-reported confidence ≥
              threshold (default 0.85).
            </p>
          </div>
          <div className="rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 mb-2">
              <GitMerge className="h-4 w-4 text-indigo-600" />
              <p className="font-semibold text-sm">auto_assign_enabled</p>
            </div>
            <p className="text-xs text-muted-foreground leading-snug">
              On first ingest, if the customer has been handled before and Gorgias has no assignee, route
              the ticket back to that agent and drop an audit note.
            </p>
          </div>
          <div className="rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 mb-2">
              <Cog className="h-4 w-4 text-indigo-600" />
              <p className="font-semibold text-sm">auto_fix_customer_enabled</p>
            </div>
            <p className="text-xs text-muted-foreground leading-snug">
              When Gorgias has a placeholder customer (@lamptwist.be / @bot.gorgias.com) but Replai found
              the real one in the thread, rebind the ticket's customer in Gorgias.
            </p>
          </div>
        </div>
      </Section>

      <Section
        title="1d · Observability - the Pipeline page"
        subtitle="Two views: Live (operational, auto-refreshing) and Audit (per-ticket flow diagram, historical)."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="h-4 w-4 text-emerald-700" />
              <p className="font-semibold text-sm">Live view</p>
            </div>
            <p className="text-xs text-muted-foreground leading-snug">
              Queue KPIs (untreated / needs-human / ready / posted), today's funnel, auto-action counts
              (auto-assigned, customer-fixed, auto-posted), category breakdown, and a real-time ticker of
              the last 20 decisions. Polls <code className="text-[0.65rem]">/api/replai/pipeline/live/</code> every 10s.
            </p>
          </div>
          <div className="rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 mb-2">
              <Layers className="h-4 w-4 text-indigo-600" />
              <p className="font-semibold text-sm">Audit view</p>
            </div>
            <p className="text-xs text-muted-foreground leading-snug">
              Each DispatchRun rendered as a 7-node flow: ticket → classify → triage → tool-selection →
              endpoints → draft. Colour-coded by outcome; dashed arrows on skipped stages. Searchable by
              subject / email / category / tool name. Backed by{' '}
              <code className="text-[0.65rem]">DispatchRun.trace</code> JSON.
            </p>
          </div>
        </div>
      </Section>

      <Section
        title="2 · Data sources"
        subtitle="Where every fact in a draft comes from. Replai reads all of them; writes only to its own tables + 2 Gorgias endpoints."
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="h-4 w-4 text-amber-600" />
              <p className="font-semibold text-sm">Gorgias</p>
              <span className="text-[0.6rem] text-muted-foreground">external</span>
            </div>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Ticket list + messages (read, cursor-paginated)</li>
              <li>• Customer + assignee fields (read)</li>
              <li>• Macros (read via sync)</li>
              <li className="text-emerald-700">• Post internal note (write)</li>
              <li className="text-emerald-700">• Assign agent (write)</li>
              <li className="text-emerald-700">• Fix customer (write)</li>
              <li className="text-emerald-700">• Merge tickets / delete note (write)</li>
            </ul>
          </div>
          <div className="rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 mb-2">
              <Database className="h-4 w-4 text-blue-600" />
              <p className="font-semibold text-sm">Shared Postgres (core DB)</p>
              <span className="text-[0.6rem] text-muted-foreground">read-only</span>
            </div>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• <code>customer_order_zone</code> - order status + lines (~hourly)</li>
              <li>• <code>customer_order_zone_agg</code> - by-email history</li>
              <li>• <code>perfy_odoo_sales_order_lines</code> - order amendments (nightly)</li>
              <li>• <code>pim_product_master</code> - specs, stock, price, bulbs</li>
              <li>• <code>pricy_market_pricing</code> + <code>pricy_market</code> - per-market price + VAT</li>
              <li>• <code>shopify_packaging_data</code> - dimensions</li>
              <li>• <code>shippy_countries</code> - supported destinations</li>
            </ul>
          </div>
          <div className="rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="h-4 w-4 text-violet-600" />
              <p className="font-semibold text-sm">Live external APIs</p>
            </div>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Odoo XML-RPC - live shipment/tracking (stale-COZ scenarios only)</li>
              <li>• Shopify Admin - manuals/metafields + order stock-at-purchase snapshot</li>
              <li>• Shippy B2B packing API - live shipping quotes (fuel + surcharges incl.)</li>
              <li>• Anthropic Claude - Sonnet 5 draft, Haiku 4.5 triage/judge (via Ferry, SDK fallback)</li>
            </ul>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Curated policy lives in the versioned <code>KnowledgeCard</code> store (facts + drafting-rules),
          retrieved per ticket and injected as annotations. Every draft is grounded in one or more of
          these sources; nothing is invented.
        </p>
      </Section>

      <Section
        title="3 · Classifier"
        subtitle="Rules live in the DB (editable from /categories) seeded from Python SEED_BODY_RULES. 30-second TTL cache. Each match bumps fires_count."
      >
        <div className="flex items-start gap-3 overflow-x-auto pb-2">
          <Box icon={Tag} title="Ticket body + subject + tags" tone="neutral" />
          <Arrow />
          <Box icon={Cog} title="Body rules" subtitle="ALL required tokens + boost" tone="replai" />
          <Arrow />
          <Box icon={Cog} title="Subject fallback" subtitle="ANY subject token (specificity-ranked)" tone="replai" />
          <Arrow />
          <Box icon={Sparkles} title="Category + confidence" subtitle="15 categories incl. pre_sales_inquiry" tone="human" />
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Current coverage: rules in DB (seed + manual) · subject fallbacks prefer specific categories over the generic
          <code> order_status </code>catch-all · unknown tickets get flagged in the Review queue.
        </p>
      </Section>

      <Section
        title="4 · Endpoint dispatch"
        subtitle="Per category, the dispatcher runs a set of data endpoints and collects their output for the LLM. Missing endpoints surface as gaps in the Endpoints page."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-xl border border-border p-4">
            <p className="font-semibold text-sm mb-2">order_status</p>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• <code>GET /orders/&#123;order_number&#125;</code> - live</li>
              <li>• <code>GET /orders/by-email</code> - live</li>
              <li>• Python annotations: past dates → "overdue", multi-parcel warnings</li>
            </ul>
          </div>
          <div className="rounded-xl border border-border p-4">
            <p className="font-semibold text-sm mb-2">pre_sales_inquiry</p>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Product mentions extracted from body</li>
              <li>• <code>search_products</code> → PIM hit with stock</li>
              <li>• Country inference (phone prefix + phrase match)</li>
              <li>• Shippy B2B packing API → real rate for country</li>
            </ul>
          </div>
          <div className="rounded-xl border border-border p-4">
            <p className="font-semibold text-sm mb-2">product_question</p>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• <code>GET /products/search</code> - fuzzy brand/title</li>
              <li>• <code>GET /products/&#123;id&#125;</code> - specs</li>
              <li>• <code>GET /products/&#123;id&#125;/availability</code> - stock</li>
            </ul>
          </div>
          <div className="rounded-xl border border-border p-4">
            <p className="font-semibold text-sm mb-2">damaged_on_arrival</p>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• No endpoints - answered purely from macro</li>
              <li>• Must-keeps extraction forces preservation of photo requirements</li>
              <li>• Macro-only dispatch path still drafts a real reply</li>
            </ul>
          </div>
        </div>
      </Section>

      <Section
        title="5 · Drafter (Claude Haiku)"
        subtitle="Claude sees the data, the top macros, extracted must-keeps, and computed annotations. Multiple safety nets run on the output."
      >
        <div className="flex items-start gap-3 overflow-x-auto pb-2">
          <Box icon={Layers} title="System prompt" subtitle="Grounding + language + no time promises + no em-dashes" tone="replai" />
          <Arrow />
          <Box icon={Wand2} title="Top macros" subtitle="Tone + must-keeps extracted" tone="replai" />
          <Arrow />
          <Box icon={Database} title="DATA + annotations" subtitle="Endpoint output + Python warnings" tone="replai" />
          <Arrow />
          <Box icon={Bot} title="Claude Haiku" subtitle="claude-haiku-4-5-20251001" tone="ai" />
          <Arrow />
          <Box icon={Cog} title="Post-gen safety" subtitle="Em-dash · time promises · multi-URL split · hallucination guard" tone="replai" />
          <Arrow />
          <Box icon={Sparkles} title="Draft reply" subtitle="markdown links, grounded, in customer language" tone="human" />
        </div>
      </Section>

      <Section
        title="6 · Human review loop"
        subtitle="Drafts get 👍/👎 feedback in the Inbox. Flagged ones land in /review - the ground truth we use to tune rules and prompts."
      >
        <div className="flex items-start gap-3 overflow-x-auto pb-2">
          <Box icon={InboxIcon} title="Inbox" subtitle="Draft shown with 👍/👎 buttons" tone="replai" />
          <Arrow />
          <Box icon={ThumbsDown} title="/review" subtitle="Flagged drafts queue" tone="replai" />
          <Arrow />
          <Box icon={Tag} title="New rules" subtitle="Edit classifier from /categories" tone="replai" />
          <Arrow />
          <Box icon={BarChart2} title="Backtest" subtitle="500 closed tickets · Judge scores" tone="replai" />
        </div>
      </Section>

      <Section
        title="7 · Write paths (hard limits)"
        subtitle="Everything else in Replai is read-only. These are the ONLY places Replai writes anywhere outside its own database."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 px-4 py-3">
            <p className="text-sm font-semibold text-emerald-900">Gorgias writes</p>
            <ul className="text-xs text-emerald-900/80 mt-1 space-y-0.5">
              <li>• Post internal note (manual + gated)</li>
              <li>• Assign ticket to agent (manual + toggle)</li>
              <li>• Fix customer (manual + toggle)</li>
              <li>• Merge ticket (manual)</li>
            </ul>
          </div>
          <div className="rounded-xl border border-border bg-white px-4 py-3">
            <p className="text-sm font-semibold text-foreground">Replai local writes</p>
            <ul className="text-xs text-muted-foreground mt-1 space-y-0.5">
              <li>• Ticket ingest · classification · dispatch runs</li>
              <li>• Feedback flags · macro category mapping</li>
              <li>• Rule edits · archive state</li>
              <li>• Backtest evaluation runs</li>
            </ul>
          </div>
        </div>
      </Section>
    </AppShell>
  );
};

export default Architecture;
